import { RefObject, useCallback, useEffect, useState } from 'react';
import { Icon } from '~/components/atomic';
import * as S from './PasswordInput.styled';

interface PasswordInputProps {
  text: string;
  setText: (text: string) => void;
  placeholder: string;
  checkingPasswordOpt?: boolean;
  confirm?: boolean;
  password?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export default function PasswordInput({
  placeholder,
  text,
  setText,
  checkingPasswordOpt,
  confirm,
  password,
  inputRef,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  // 유효성 검사 결과 상태
  const [isValidLength, setIsValidLength] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasLetterCase, setHasLetterCase] = useState(false);
  const [hasSymbol, setHasSymbol] = useState(false); // State for symbol check
  // 비밀번호 확인 유효성 검사 결과
  const [confirmPassword, setConfirmPassword] = useState(false);

  useEffect(() => {
    setIsValidLength(text.length >= 8);
    setHasNumber(/\d/.test(text));
    setHasLetterCase(/[a-z]/.test(text));
    setHasSymbol(/[!@#$%^&*(),.?":{}|<>]/.test(text)); // Check for symbols
  }, [text]);

  useEffect(() => {
    setConfirmPassword(text === password && text.trim().length !== 0);
  }, [text, password]);

  const handleTextChange = useCallback((setText: (text: string) => void) => {
    return (e: React.ChangeEvent) => {
      const inputText = (e.target as HTMLInputElement).value;
      setText(inputText);
    };
  }, []);

  return (
    <S.Container>
      <S.InputBox type={'password'}>
        <input
          placeholder={placeholder}
          type={showPassword ? 'text' : 'password'}
          value={text}
          onChange={handleTextChange(setText)}
          ref={inputRef}
        />
        <S.Option>
          <div onClick={() => setShowPassword(!showPassword)}>
            <Icon
              color='caption1'
              size={24}
              iconName={showPassword ? 'PasswordHide' : 'PasswordShow'}
            />
          </div>
        </S.Option>
      </S.InputBox>

      {checkingPasswordOpt &&
        (confirm ? (
          <S.CheckListWrapper>
            <S.CheckListBox>
              <S.CheckList
                className={confirmPassword ? 'check active' : 'check'}
              >
                <S.CheckIcon active={confirmPassword} />
                <S.Desc>Same with above password</S.Desc>
              </S.CheckList>
            </S.CheckListBox>
          </S.CheckListWrapper>
        ) : (
          <>
            <S.CheckListWrapper>
              <S.CheckListBox>
                <S.CheckList
                  className={isValidLength ? 'check active' : 'check'}
                >
                  <S.CheckIcon active={isValidLength} />
                  <S.Desc>At least 8 characters long</S.Desc>
                </S.CheckList>
                <S.CheckList
                  className={hasLetterCase ? 'check active' : 'check'}
                >
                  <S.CheckIcon active={hasLetterCase} />
                  <S.Desc>At least one letter (a-z)</S.Desc>
                </S.CheckList>
                <S.CheckList className={hasNumber ? 'check active' : 'check'}>
                  <S.CheckIcon active={hasNumber} />
                  <S.Desc>At least 1 number</S.Desc>
                </S.CheckList>
                <S.CheckList className={hasSymbol ? 'check active' : 'check'}>
                  <S.CheckIcon active={hasSymbol} />
                  <S.Desc>At least one symbol</S.Desc>
                </S.CheckList>
              </S.CheckListBox>
            </S.CheckListWrapper>
          </>
        ))}
    </S.Container>
  );
}
